/**
 * Manufacturer → Revision lines: the manufacturer's calibre lineages (memo 5.2).
 *
 * Shown only when the manufacturer has `supportsLineage` on — the gate that
 * keeps lineage controls off makers that renumber outright (Rolex, Omega, AP).
 *
 * Each line lists its revisions in production order with their state chip. A
 * line is created, renamed and deleted here, and its members picked and edited
 * in the same dialog (RevisionLineEditor); each calibre's own Revisions card
 * edits the same fields. Writes are immediate, like the Calibre families panel.
 *
 * Lineage Phase 5 adds two AI entry points: Find revision lines (candidate
 * clusters → Create line / Ask AI / Not a revision) and ✨ Ask AI on each line
 * (labels, years and current for its members, curator values kept).
 */
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { RevisionChipBadge } from '../CuratedWatches/RevisionChipBadge';
import {
  deleteLineage,
  getLineageMembersForManufacturer,
  getLineagesForManufacturer,
  type CalibreLineageEntry,
} from '../../services/calibreLineage';
import { currentConflict, currentMember, revisionChip, sortMembers } from '../../services/calibreLineageLogic';
import type { CustomCalibreEntry } from '../../services/firebase';
import { FindRevisionLinesDialog } from './FindRevisionLinesDialog';
import { LineageSuggestionDialog } from './LineageSuggestionDialog';
import { mixedCalibreNumbers, mixedNumbersText } from '../../services/revisionClusters';
import { RevisionLineEditor } from './RevisionLineEditor';
import { DEFAULT_REVISION_TERMS, type RevisionTerms } from '../../services/revisionTerms';

interface Props {
  manufacturerId: string;
  /** For the Find revision lines title; the id is shown when absent. */
  manufacturerName?: string;
  /** The maker's revision words (Seiko "Revision Codes"); default Revision / Revisions. */
  terms?: RevisionTerms;
}

type Editing = { lineage: CalibreLineageEntry | null } | null;

/** Why ✨ Ask AI is off on a line with no revisions yet. */
export const askAiNeedsMembers = (terms: RevisionTerms = DEFAULT_REVISION_TERMS) =>
  `Add ${terms.plural.toLowerCase()} to this line first to enable AI suggestions.`;

function yearsLabel(c: CustomCalibreEntry): string {
  if (typeof c.activeFromYear !== 'number') return '';
  return typeof c.activeUntilYear === 'number' ? `${c.activeFromYear}–${c.activeUntilYear}` : `${c.activeFromYear}–`;
}

export function RevisionLinesSection({ manufacturerId, manufacturerName, terms = DEFAULT_REVISION_TERMS }: Props) {
  const askAiHint = askAiNeedsMembers(terms);
  const [lineages, setLineages] = useState<CalibreLineageEntry[] | null>(null);
  const [members, setMembers] = useState<Map<string, CustomCalibreEntry[]>>(new Map());
  const [editing, setEditing] = useState<Editing>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<CalibreLineageEntry | null>(null);
  const [finding, setFinding] = useState(false);
  const [asking, setAsking] = useState<CalibreLineageEntry | null>(null);

  const load = useCallback(async () => {
    try {
      const [ls, ms] = await Promise.all([
        getLineagesForManufacturer(manufacturerId),
        getLineageMembersForManufacturer(manufacturerId),
      ]);
      setLineages(ls);
      setMembers(ms);
    } catch (err) {
      console.error('Failed to load revision lines', err);
      toast.error('Could not load revision lines');
      setLineages([]);
    }
  }, [manufacturerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    try {
      await deleteLineage(confirmDelete.id, (members.get(confirmDelete.id) ?? []).map((m) => m.id));
      toast.success('Revision line deleted');
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Revision lines</h3>
          <p className="text-xs text-gray-500">
            Successive {terms.plural.toLowerCase()} of one calibre (2824 → 2824-1 → 2824-2). Concurrent grades are tiers,
            not {terms.plural.toLowerCase()}. Add them here (+ Revision line / Edit) or from a calibre&apos;s own Revisions card.
          </p>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button size="sm" variant="secondary" onClick={() => setFinding(true)}>
            Find revision lines
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setEditing({ lineage: null })}>
            + Revision line
          </Button>
        </div>
      </div>

      {lineages === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : lineages.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No revision lines yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {lineages.map((l) => {
            const ms = sortMembers(members.get(l.id) ?? []);
            const current = currentMember(ms);
            return (
              <li key={l.id} className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {l.baseLabel} <span className="font-mono text-xs text-gray-400">{l.id}</span>
                    </p>
                    {l.description && <p className="text-xs text-gray-500">{l.description}</p>}
                  </div>
                  <div className="flex gap-1">
                    <span title={ms.length === 0 ? askAiHint : undefined}>
                      <Button size="sm" variant="ghost" disabled={ms.length === 0} onClick={() => setAsking(l)}>
                        ✨ Ask AI
                      </Button>
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing({ lineage: l })}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setConfirmDelete(l)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                {mixedCalibreNumbers(ms.map((m) => m.name)).length > 0 && (
                  <p className="text-xs text-amber-700">{mixedNumbersText(mixedCalibreNumbers(ms.map((m) => m.name)))}</p>
                )}
                {currentConflict(ms) && (
                  <p className="text-xs text-amber-700">More than one revision is marked current — open one and fix it.</p>
                )}
                {ms.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No {terms.plural.toLowerCase()} yet. {askAiHint}</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {ms.map((m) => (
                        <tr key={m.id}>
                          <td className="py-0.5 pr-3 text-gray-900">{m.name}</td>
                          <td className="py-0.5 pr-3 font-mono text-xs text-gray-500">{m.revisionLabel ?? ''}</td>
                          <td className="py-0.5 pr-3 text-xs text-gray-500 tabular-nums">{yearsLabel(m)}</td>
                          <td className="py-0.5">
                            <RevisionChipBadge chip={revisionChip(m, current, ms)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <RevisionLineEditor
          manufacturerId={manufacturerId}
          lineage={editing.lineage}
          members={editing.lineage ? members.get(editing.lineage.id) ?? [] : []}
          lineages={lineages ?? []}
          terms={terms}
          onClose={() => setEditing(null)}
          onSaved={() => void load()}
        />
      )}

      {finding && (
        <FindRevisionLinesDialog
          manufacturerId={manufacturerId}
          manufacturerName={manufacturerName || manufacturerId}
          onClose={() => setFinding(false)}
          onChanged={() => void load()}
        />
      )}
      {asking && (
        <LineageSuggestionDialog
          manufacturerId={manufacturerId}
          lineage={asking}
          calibreIds={[]}
          calibres={members.get(asking.id) ?? []}
          onClose={() => setAsking(null)}
          onApplied={() => void load()}
        />
      )}

      <Modal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete revision line" size="md">
        {confirmDelete && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Delete <span className="font-medium">{confirmDelete.baseLabel}</span>? Its{' '}
              {(members.get(confirmDelete.id) ?? []).length} revision(s) stay as calibres, but lose their revision
              label, years and current flag. References are not touched.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} loading={saving}>
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
